import { ReactNode, useRef, useState } from "react"

// A tabbed section card (Orders / Guest orders, Code Batches / Codes, …):
// tab headings on the left, the active tab's own actions on the right, the
// body below. The body never shrinks on a tab switch — its min-height is the
// tallest content shown so far, measured as the user clicks — so switching
// tabs does not make the page jump; content that loads taller still grows it.
// Re-key the card (e.g. by sku id) to start the measurement over.
export default function TabCard<K extends string>({ tabs, tab, onTabChange, actions, children }: {
  tabs: { key: K, label: string }[]
  tab: K
  onTabChange: (tab: K) => void
  actions?: ReactNode
  children: ReactNode
}) {
  const bodyRef = useRef<HTMLDivElement>(null)
  const [minHeight, setMinHeight] = useState(0)

  const switchTo = (key: K) => {
    if (key === tab) return
    const height = bodyRef.current?.offsetHeight ?? 0
    setMinHeight(h => Math.max(h, height))
    onTabChange(key)
  }

  return (
    <div className="tab-card" style={{ marginTop: 32 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div className="tab-card-tabs" role="tablist">
          {tabs.map(({ key, label }) => (
            <h2
              key={key}
              className={`section-title tab-card-tab${tab === key ? "" : " tab-card-tab-inactive"}`}
              role="tab"
              aria-selected={tab === key}
              onClick={() => switchTo(key)}
            >
              {label}
            </h2>
          ))}
        </div>
        {actions}
      </div>
      {/* offsetHeight includes the body's padding, so the floor is applied
          border-box — the body has no explicit height, nothing else changes */}
      <div ref={bodyRef} className="tab-card-body" style={minHeight > 0 ? { minHeight, boxSizing: "border-box" } : undefined}>
        {children}
      </div>
    </div>
  )
}
